import { useForm } from 'react-hook-form'
import { Button, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import {
    enableServiceAddon,
    getProviderAddons,
    getProviders,
    getServiceQuotas,
    getServiceInformation,
    updateServiceInformations,
} from '@/modules/Service/services'
import { useEffect, useMemo } from 'react'
import UpdateProviderSchema from './schema'
import KubernetesAddonCard from './kubernetes-addon-card'
import CloudInfoSummary from './cloud-info-summary'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface SelectProviderProps {
    serviceTemplate: string
    serviceID: number
    serviceUUID: string
}

const SelectProvider: React.FC<SelectProviderProps> = ({
    serviceTemplate,
    serviceID,
    serviceUUID,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()

    const formControl = useForm({
        defaultValues: { provider_id: '', zone_id: '' },
        resolver: yupResolver(UpdateProviderSchema),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: updateServiceInformations },
        {
            successMessage: t('update-provider-and-zone-information-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => refetchServiceInformation(),
        }
    )

    const { mutateAsync: enableAddon, isPending: isEnabling } =
        useMutationWithHandler(
            { mutationFn: enableServiceAddon },
            {
                successMessage: t('services:enable-kubernetes-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES_CLOUD_INFO,
            }
        )

    const { data: providerAddons } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CLOUD_INFO, serviceID],
        queryFn: () => getProviderAddons(serviceID),
        enabled: !!serviceUUID,
    })

    const { data: serviceQuotas } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_QUOTAS, serviceID],
        queryFn: () => getServiceQuotas(serviceID),
        enabled: !!serviceUUID,
    })

    const {
        data: serviceInformation,
        renderState: renderServiceInfoState,
        refetch: refetchServiceInformation,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_INFORMATIONS, serviceID],
        queryFn: () => getServiceInformation(serviceID),
    })

    const { data: providers, renderState: renderProvidersState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.PROVIDERS],
            queryFn: () => getProviders(),
        })

    const provider_id = formControl.watch('provider_id')

    const zone = serviceInformation?.results?.find(
        (info) => info.name === 'zone'
    )?.value
    const provider = serviceInformation?.results?.find(
        (info) => info.name === 'provider'
    )?.value

    const providerOptions = useMemo(
        () =>
            providers
                ?.filter((p) => p.creatable_type === serviceTemplate)
                ?.map((p) => ({ label: p.scope, value: p.id })) || [],
        [providers]
    )

    const zoneOptions = useMemo(
        () =>
            providers
                ?.find((p) => p.id === (provider_id as string))
                ?.zones?.map((z) => ({
                    label: `${z.name} - (${z.description})`,
                    value: z.id,
                })) || [],
        [providers, provider_id]
    )

    const hasKubernetesProvider = useMemo(
        () =>
            providers?.some(
                (p) =>
                    p.creatable_type === serviceTemplate &&
                    p.scope?.includes('Kubernetes')
            ),
        [providers, serviceTemplate]
    )

    const hasKubernetesAddon = useMemo(
        () =>
            providerAddons?.addons?.some((addon: { provider_scope: string }) =>
                addon.provider_scope?.includes('Kubernetes')
            ),
        [providerAddons]
    )

    const kubernetesAddons = useMemo(
        () =>
            providerAddons?.addons?.filter(
                (addon: { provider_scope: string }) =>
                    addon.provider_scope?.includes('Kubernetes')
            ) || [],
        [providerAddons]
    )

    const handleEnableKubernetes = () => {
        setConfirmation({
            open: true,
            title: t('services:enable-kubernetes'),
            description: t('services:enable-kubernetes-description'),
            onConfirm: () =>
                enableAddon({ serviceID, addonType: 'kubernetes' }),
        })
    }

    useEffect(() => {
        provider && formControl.setValue('provider_id', provider)
        zone && formControl.setValue('zone_id', zone)
    }, [provider, zone])

    const serviceInfoState = renderServiceInfoState()
    if (serviceInfoState) return serviceInfoState

    const providersState = renderProvidersState()
    if (providersState) return providersState

    // Registered service: show cloud info + addons in one card
    if (serviceUUID && providerAddons) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-4 transition-all duration-200">
                <CloudInfoSummary
                    providerAddons={providerAddons}
                    serviceQuotas={serviceQuotas}
                />
                {hasKubernetesProvider && (
                    <div className="pt-3 mt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                            <Typogaphy className="text-sm font-medium text-gray-600">
                                Kubernetes
                            </Typogaphy>
                            <Button
                                variant="contained"
                                size="small"
                                loading={isEnabling}
                                disabled={isEnabling || hasKubernetesAddon}
                                onClick={handleEnableKubernetes}
                            >
                                {hasKubernetesAddon
                                    ? t('services:kubernetes-enabled')
                                    : t('services:enable-kubernetes')}
                            </Button>
                        </div>
                        {hasKubernetesAddon && kubernetesAddons.length > 0 && (
                            <div className="space-y-2">
                                {kubernetesAddons.map(
                                    (addon: {
                                        service_id: number
                                        service_name: string
                                        service_uuid: string
                                        provider_scope: string
                                        clusters?: {
                                            id: string
                                            name: string
                                            uid: string
                                            status: string
                                            created_at: string
                                            metadata: Record<string, unknown>
                                        }[]
                                    }) => (
                                        <KubernetesAddonCard
                                            key={addon.service_id}
                                            addon={addon}
                                            serviceID={serviceID}
                                        />
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // Not yet registered: show provider/zone form
    return (
        <div className="p-4 bg-white rounded-[8px]">
            <Typogaphy className="font-medium text-primary mb-4">
                {t('service-technical')}
            </Typogaphy>
            <GridHookForm
                data={[
                    {
                        formItemProps: {
                            name: 'provider_id',
                            label: t('provider'),
                            type: 'select',
                            placeholder: t('select-provider'),
                            options: providerOptions || [],
                        },
                    },
                    {
                        formItemProps: {
                            name: 'zone_id',
                            label: t('Zone'),
                            type: 'select',
                            placeholder: t('Zone-placeholder'),
                            options: zoneOptions || [],
                            disabled: !provider_id,
                        },
                    },
                ]}
                onSubmit={(values) =>
                    mutateAsync({
                        serviceID,
                        data: [
                            { name: 'provider', value: values.provider_id },
                            { name: 'zone', value: values.zone_id },
                        ],
                        message: 'Update provider and zone information',
                    })
                }
                actions={[
                    {
                        type: 'submit',
                        title: t('save'),
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending || !formControl.formState.isDirty,
                    },
                ]}
                formControl={formControl}
            />
        </div>
    )
}

export default SelectProvider
